import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { euro, NATURES } from "@/lib/accounting";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const EMPTY = { code: "", description: "", qty: 1, unit: "", unit_price: 0, discount_pct: 0, vat_rate: 22, vat_nature: null };

const useCatalog = () => {
  const { price_lists } = useData();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/warehouse/items").then((r) => setItems(r.data)).catch(() => {}); }, []);
  return useMemo(() => {
    const acts = price_lists.flatMap((pl) => pl.items.map((i) => ({ code: i.code, description: i.description, unit: i.unit, price: i.price, src: pl.name })));
    const mats = items.filter((i) => i.active).map((i) => ({ code: i.code, description: i.name, unit: i.unit, price: i.sale_price || 0, src: "Listino materiali" }));
    return [...acts, ...mats];
  }, [price_lists, items]);
};

const num = (v) => (v === "" ? 0 : Number(v));

export const LinesEditor = ({ lines, onChange, reverseCharge }) => {
  const catalog = useCatalog();
  const set = (i, patch) => onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const pickCode = (i, code) => {
    const c = catalog.find((x) => x.code === code);
    set(i, c ? { code, description: c.description, unit: c.unit, unit_price: c.price } : { code });
  };
  const amount = (l) => num(l.qty) * num(l.unit_price) * (1 - num(l.discount_pct) / 100);
  return (
    <div data-testid="invoice-lines-editor">
      <datalist id="invoice-catalog">{catalog.map((c, k) => <option key={`${c.code}${k}`} value={c.code}>{`${c.description} · ${euro(c.price)} · ${c.src}`}</option>)}</datalist>
      <div className="overflow-x-auto border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs"><tr><th className="p-2">Codice</th><th className="p-2">Descrizione</th><th className="p-2">Q.tà</th><th className="p-2">U.M.</th><th className="p-2">Prezzo</th><th className="p-2">Sc.%</th><th className="p-2">IVA</th><th className="p-2 text-right">Importo</th><th /></tr></thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i} className="border-t" data-testid={`invoice-line-${i}`}>
                <td className="p-1"><Input list="invoice-catalog" className="h-8 w-28 font-mono text-xs" value={l.code} onChange={(e) => pickCode(i, e.target.value)} data-testid={`line-code-${i}`} /></td>
                <td className="p-1"><Input className="h-8 min-w-[220px]" value={l.description} onChange={(e) => set(i, { description: e.target.value })} data-testid={`line-desc-${i}`} /></td>
                <td className="p-1"><Input type="number" className="h-8 w-20" value={l.qty} onChange={(e) => set(i, { qty: e.target.value })} data-testid={`line-qty-${i}`} /></td>
                <td className="p-1"><Input className="h-8 w-14" value={l.unit} onChange={(e) => set(i, { unit: e.target.value })} /></td>
                <td className="p-1"><Input type="number" step="0.01" className="h-8 w-24" value={l.unit_price} onChange={(e) => set(i, { unit_price: e.target.value })} data-testid={`line-price-${i}`} /></td>
                <td className="p-1"><Input type="number" className="h-8 w-16" value={l.discount_pct} onChange={(e) => set(i, { discount_pct: e.target.value })} data-testid={`line-discount-${i}`} /></td>
                <td className="p-1">
                  {reverseCharge ? <span className="text-xs font-semibold">N6.3</span> : (
                    <select className="h-8 border bg-white px-1 text-xs" value={l.vat_nature || String(l.vat_rate)} onChange={(e) => set(i, NATURES[e.target.value] ? { vat_rate: 0, vat_nature: e.target.value } : { vat_rate: Number(e.target.value), vat_nature: null })} data-testid={`line-vat-${i}`}>
                      {["22", "10", "5", "4"].map((r) => <option key={r} value={r}>{r}%</option>)}
                      {Object.entries(NATURES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  )}
                </td>
                <td className="p-2 text-right font-mono text-xs">{euro(amount(l))}</td>
                <td className="p-1"><Button type="button" size="icon" variant="ghost" onClick={() => onChange(lines.filter((_, j) => j !== i))} data-testid={`line-remove-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => onChange([...lines, { ...EMPTY }])} data-testid="invoice-add-line"><Plus className="mr-1 h-4 w-4" />Aggiungi riga</Button>
      <div className="mt-2 text-right text-sm">Imponibile <strong className="font-mono" data-testid="invoice-editor-taxable">{euro(lines.reduce((s, l) => s + amount(l), 0))}</strong></div>
    </div>
  );
};

export const normalizeLines = (lines) => lines.map((l) => ({ ...l, qty: num(l.qty), unit_price: num(l.unit_price), discount_pct: num(l.discount_pct), vat_rate: num(l.vat_rate) }));
export const emptyLine = () => ({ ...EMPTY });
